import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SearchableSelect from '../../components/common/SearchableSelect';

// Keep the native field ID/value for FormGenerator, existing scripts and DocHub references.
// The searchable control changes the same field instead of introducing a second saved value.
const DropDownInput = ({ id, options = [], optionValues, title, className = '', onChange = '',
  value: defaultValue = '', preValue, onFormChange, disabled = false, onChangeFunction, size,
  required, keepPlaceholder = false, dependentElems = [], showDependentOn = '', showDependentOnArray,
  placeHolder = 'Select one...' }) => {
  const sourceValue = preValue !== undefined && preValue !== null && preValue !== '' ? preValue : defaultValue;
  const values = options.map((option, index) => String(optionValues ? optionValues[index] : option));
  const placeholder = !required || keepPlaceholder;
  const initial = sourceValue !== '' && sourceValue != null ? String(sourceValue) : placeholder ? 'dropdown-placeholder' : values[0] || '';
  const [selected, setSelected] = useState(initial);
  const native = useRef(null);
  const [nativeDisabled, setNativeDisabled] = useState(disabled);
  const known = values.includes(selected) || selected === 'dropdown-placeholder' || selected === '';
  const choices = options.map((option, index) => ({ label: String(option), value: values[index] }));
  if (placeholder) choices.unshift({ value: 'dropdown-placeholder', label: placeHolder });
  if (!known) choices.push({ value: selected, label: 'Current selection (not in available choices)' });

  const internalOnChange = (event, checkForm = true) => {
    if (checkForm && onFormChange) onFormChange(event);
    const value = native.current.value;
    dependentElems.forEach(key => {
      const element = document.getElementById(key);
      const container = document.getElementById(key + '-col-container');
      if (!element || !container) return;
      const show = showDependentOnArray ? showDependentOnArray.includes(value) : value === showDependentOn;
      container.classList.toggle('d-none', !show);
      element.required = !!show;
      if (!show) element.value = '';
    });
    if (onChange && window[onChange]) window[onChange](event);
    if (onChangeFunction) onChangeFunction(event);
  };
  useLayoutEffect(() => {
    const field = native.current;
    const descriptor = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
    // Existing form scripts set .value directly. Reflect those writes in the visible control.
    const originalPrototype = Object.getPrototypeOf(field);
    const selectedIndex = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'selectedIndex');
    // Use an instance-only prototype so native indexed-select behavior stays intact.
    Object.setPrototypeOf(field, Object.create(originalPrototype, {
      value: { configurable: true, get() { return descriptor.get.call(this); }, set(value) {
        descriptor.set.call(this, value); setSelected(descriptor.get.call(this));
      } },
      selectedIndex: { configurable: true, get() { return selectedIndex.get.call(this); }, set(value) {
        selectedIndex.set.call(this, value); setSelected(descriptor.get.call(this));
      } }
    }));
    const observer = typeof MutationObserver !== 'undefined' ? new MutationObserver(() => setNativeDisabled(field.disabled)) : null;
    if (observer) observer.observe(field, { attributes: true, attributeFilter: ['disabled'] });
    const reset = () => { setSelected(initial); };
    const form = field.form;
    if (form) form.addEventListener('reset', reset);
    return () => { if (observer) observer.disconnect(); Object.setPrototypeOf(field, originalPrototype); if (form) form.removeEventListener('reset', reset); };
  }, [id, initial]);
  useLayoutEffect(() => {
    const field = native.current;
    Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set.call(field, selected);
  });
  useEffect(() => { setSelected(initial); if (native.current) native.current.value = initial; }, [initial]);
  // Initialize dependent fields once, matching the existing form callback contract.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { internalOnChange(null, false); }, []);

  return <div className={className} data-searchable-field={id}>
    <select ref={native} id={id} disabled={disabled} defaultValue={initial} tabIndex={-1} aria-hidden="true"
      className="sr-only" data-searchable-control={id + '-search'} onFocus={() => document.getElementById(id + '-search')?.focus()}
      onChange={event => { setSelected(event.target.value); internalOnChange(event); }}>
      {!values.includes('') && <option value="" hidden>{placeHolder}</option>}
      {choices.map((option, index) => <option key={index} value={option.value}>{option.label}</option>)}
    </select>
    <SearchableSelect inputId={id + '-search'} aria-label={title || id} options={choices}
      value={selected} isDisabled={disabled || nativeDisabled} size={size} isClearable={placeholder}
      placeholder={placeHolder} noOptionsMessage={() => 'No matching options'} onChange={value => {
        const field = native.current;
        field.value = value || (placeholder ? 'dropdown-placeholder' : '');
        internalOnChange({ target: field, currentTarget: field, preventDefault() {}, stopPropagation() {} });
      }} />
  </div>;
};
export default DropDownInput;
